// src/pages/MyOrders.jsx
import { colors as themeColors } from '../utils/theme';
import { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress, useMediaQuery, Paper, Grid } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = `http://192.168.1.248:3103/api`;

export default function MyOrders({ loggedInUser }) {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const isMobile = useMediaQuery('(max-width:600px)');
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API_URL}/orders`);
                setOrders(res.data || []);
            } catch (err) {
                console.error('Failed to fetch orders', err);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const columns = [
        { field: 'SONumber', headerName: 'Order No.', flex: 1.2 },
        {
            field: 'SODate', headerName: 'Order Date', flex: 1,
            valueFormatter: (params) => {
                const v = params.value;
                if (typeof v !== 'string' || !v.includes('-')) {
                    return '-';
                }
                const [yyyy, mm, dd] = v.split('-');
                return `${dd}/${mm}/${yyyy}`;
            },
        },
        { field: 'PartyName', headerName: 'Customer', flex: 1.5 },
        ...(loggedInUser?.userCategory !== "SalesMan"
            ? [
                {
                    field: 'BillAmt', headerName: 'Total', flex: 1, align: 'right', headerAlign: 'right',
                    valueFormatter: (params) => {
                        const num = params.value;
                        if (typeof num !== 'number' || isNaN(num)) {
                            return '-';
                        }
                        return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2, })}`;
                    },
                },
            ] : []),
        {
            flex: 1, align: 'right', headerAlign: 'right', sortable: false,
            renderCell: (params) => (<Button variant="outlined" size="small" onClick={() => navigate(`/main/Order/${params.row.SOAutoID}`)} > View </Button>),
        },
    ];

    return (
        <Box p={isMobile ? 2 : 1}>
            <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h5" fontWeight={600}>
                    📦 My Orders
                </Typography>
            </Grid>
            <Paper sx={{ borderRadius: 1, p: isMobile ? 2 : 1 }}>
                {loading ? (
                    <Box textAlign="center" mt={4}><CircularProgress /></Box>
                ) : (
                    <Box sx={{ height: '83vh', maxWidth: '100%', overflow: 'auto' }}>
                        <DataGrid
                            rows={orders}
                            columns={columns}
                            getRowId={(row) => row.SOAutoID}
                            pageSize={10}
                            rowsPerPageOptions={[10, 20, 50]}
                            disableSelectionOnClick
                            autoHeight
                            sx={{
                                /* header background */
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: themeColors.primary.dark,
                                    color: themeColors.neutral.surface,
                                    fontWeight: 'bold',
                                },

                                /* header text (cell title) */
                                '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeaderTitleContainer': {
                                    color: themeColors.neutral.surface, // ensures header text is white
                                },

                                /* all SVG icons inside header cells (generic) */
                                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                                    color: `${themeColors.neutral.surface} !important`,
                                    opacity: 1,
                                },

                                /* header icon buttons (container for three-dots menu button, etc.) */
                                '& .MuiDataGrid-columnHeader .MuiIconButton-root': {
                                    color: `${themeColors.neutral.surface} !important`,
                                    opacity: 1,
                                },

                                /* specific DataGrid icon classes (menu/filter/sort) */
                                '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon, & .MuiDataGrid-columnHeader .MuiDataGrid-filterIcon, & .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon': {
                                    color: `${themeColors.neutral.surface} !important`,
                                    opacity: 1,
                                },

                                /* Force the SVG path/fill to pure white for sort icon specifically */
                                '& .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon svg, & .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon svg path': {
                                    fill: `${themeColors.neutral.surface} !important`,
                                    stroke: `${themeColors.neutral.surface} !important`,
                                    color: `${themeColors.neutral.surface} !important`,
                                    opacity: 1,
                                },

                                /* Ensure other icons (menu/filter) remain white */
                                '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon svg path, & .MuiDataGrid-columnHeader .MuiDataGrid-filterIcon svg path': {
                                    fill: `${themeColors.neutral.surface} !important`,
                                    stroke: `${themeColors.neutral.surface} !important`,
                                    opacity: 1,
                                },

                                /* keep row striping */
                                '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: themeColors.neutral.surface },
                                '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: themeColors.primary.tint },

                                /* remove header focus outline */
                                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                                    outline: 'none',
                                },
                            }}
                        />
                    </Box>
                )}
            </Paper>
        </Box>
    );
}